import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import userManagementService from '../../services/userManagementService';
import userService from '../../services/userService';
import { UserRole } from '../../../shared/types';

// Types
interface Instructor {
  instructor_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role: string;
  status: string;
  created_at: string;
  agency_name?: string;
  office_name?: string;
  profile_image?: string;
}

interface AgencyUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
}

interface InstructorState {
  instructors: Instructor[];
  availableUsers: AgencyUser[];
  selectedUser: AgencyUser | null;
  searchQuery: string;
  loading: boolean;
  instructorsLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: InstructorState = {
  instructors: [],
  availableUsers: [],
  selectedUser: null,
  searchQuery: '',
  loading: false,
  instructorsLoading: true,
  error: null,
};

// Async thunks
export const loadInstructors = createAsyncThunk(
  'instructor/loadInstructors',
  async (_, { rejectWithValue }) => {
    try {
      const users = await userManagementService.getAgencyUsers({ role: UserRole.INSTRUCTOR });
      
      const instructorList: Instructor[] = users.map(user => ({
        instructor_id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone || user.office_phone,
        role: user.role,
        status: user.status,
        created_at: user.created_at,
        agency_name: undefined,
        office_name: user.office_name,
        profile_image: user.profile_image
      }));
      
      return instructorList;
    } catch (error) {
      return rejectWithValue('Failed to load instructors');
    }
  }
);

export const loadAvailableUsers = createAsyncThunk(
  'instructor/loadAvailableUsers',
  async (search: string = '', { rejectWithValue }) => {
    try {
      const users = await userManagementService.getAgencyUsers({ 
        search,
        role: UserRole.ATTENDEE
      });
      
      // Filter out users who already have instructor role
      const availableUsersList = users.filter(user => 
        user.role !== UserRole.INSTRUCTOR && 
        user.role !== UserRole.TRAINING_MANAGER && 
        user.role !== UserRole.COORDINATOR
      );
      
      return availableUsersList;
    } catch (error) {
      return rejectWithValue('Failed to load available users');
    }
  }
);

export const addInstructor = createAsyncThunk(
  'instructor/addInstructor',
  async (userId: number, { dispatch, rejectWithValue }) => {
    try {
      await userService.updateUser(userId, { role: UserRole.INSTRUCTOR });
      
      // Reload instructors list
      await dispatch(loadInstructors());
      
      return userId;
    } catch (error) {
      return rejectWithValue('Failed to add instructor');
    }
  }
);

export const removeInstructor = createAsyncThunk(
  'instructor/removeInstructor',
  async (instructorId: number, { dispatch, rejectWithValue }) => {
    try {
      await userService.updateUser(instructorId, { role: UserRole.ATTENDEE });
      
      // Reload instructors list
      await dispatch(loadInstructors());
      
      return instructorId;
    } catch (error) {
      return rejectWithValue('Failed to remove instructor');
    }
  }
);

// Slice
const instructorSlice = createSlice({
  name: 'instructor',
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<AgencyUser | null>) => {
      state.selectedUser = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetInstructorState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Load instructors
      .addCase(loadInstructors.pending, (state) => {
        state.instructorsLoading = true;
        state.error = null;
      })
      .addCase(loadInstructors.fulfilled, (state, action) => {
        state.instructorsLoading = false;
        state.instructors = action.payload;
        state.error = null;
      })
      .addCase(loadInstructors.rejected, (state, action) => {
        state.instructorsLoading = false;
        state.error = action.payload as string;
      })
      // Load available users
      .addCase(loadAvailableUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadAvailableUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.availableUsers = action.payload;
        state.error = null;
      })
      .addCase(loadAvailableUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add instructor
      .addCase(addInstructor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addInstructor.fulfilled, (state) => {
        state.loading = false;
        state.selectedUser = null;
        state.searchQuery = '';
        state.error = null;
      })
      .addCase(addInstructor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Remove instructor
      .addCase(removeInstructor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeInstructor.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(removeInstructor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { setSelectedUser, setSearchQuery, setError, resetInstructorState } = instructorSlice.actions;

// Export selectors
export const selectInstructors = (state: { instructor: InstructorState }) => state.instructor.instructors;
export const selectAvailableUsers = (state: { instructor: InstructorState }) => state.instructor.availableUsers;
export const selectSelectedUser = (state: { instructor: InstructorState }) => state.instructor.selectedUser;
export const selectSearchQuery = (state: { instructor: InstructorState }) => state.instructor.searchQuery;
export const selectInstructorLoading = createSelector(
  [(state: { instructor: InstructorState }) => state.instructor],
  (instructor) => ({
    loading: instructor.loading,
    instructorsLoading: instructor.instructorsLoading,
  })
);

// Export reducer
export default instructorSlice.reducer;

